import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';

/** The card every section of the tab sits in. */
export const SECTION_CARD = 'bg-paper border-divider rounded-md border new:border-0 new:shadow-card new:outline-hidden';

/** A section's title row: at least 46px high, 16px in (8px when the invoice area is under 1186px). */
export const SECTION_HEADER = '@max-[1186px]/invoice:px-2 flex min-h-[46px] items-center px-4 py-[5px]';

/** The weight every title inside a section's title row takes. */
export const SECTION_TITLE = 'text-base leading-[1.375rem] font-semibold';

/**
 * A collapsible section's panel. The caller keeps it mounted, so its fields stay registered while closed. It
 * clips its overflow only while its height animates, and is `overflow: visible` once fully open, so a row wider
 * than the card (the timecard table's title row and bulk actions in a narrow pane) overflows instead of being cut
 * off. Base UI marks only the first frame of an open with `data-starting-style`, so the switch to visible is a
 * discrete transition that waits out the height transition; a close switches back to clipping at once. It clips
 * rather than hides: `overflow: hidden` would make it a scroll container, and a sticky title row inside would
 * then pin to the panel, which never scrolls, instead of to the pane. It is `relative` because Base UI renders
 * visually hidden, absolutely positioned helpers beside its controls (a named checkbox's input, an open
 * combobox's dismiss button). With no positioned ancestor, they anchor to the page's scroll area, and their
 * offsets stretch it past the footer.
 */
export const SECTION_PANEL =
    'relative h-(--collapsible-panel-height) overflow-clip transition-[height] duration-250 ease-in-out data-ending-style:h-0 data-starting-style:h-0 motion-reduce:transition-none data-open:not-data-starting-style:overflow-visible motion-safe:data-open:not-data-starting-style:[transition:height_250ms_ease-in-out,overflow_0s_250ms_allow-discrete]';

/** A section's body under its title row. */
export const SECTION_BODY = 'border-divider @max-[1186px]/invoice:p-4 border-t p-6';

/** A card of the Cases tab in the new design: the record section's look, positioned for its recalculation veil. */
export const CASE_CARD_CLASS =
    'bg-card shadow-card text-foreground @container relative flex flex-col gap-3.5 rounded-[10px] px-5 pt-4 pb-[18px] outline-hidden';

/** The figures under a Cases tab card's header, in two columns, or three on a wide card. */
export const CASE_FIGURES_CLASS = 'border-divider m-0 grid grid-cols-2 gap-x-6 gap-y-2.5 border-t pt-3 @xl:grid-cols-3';

/** A read cell of a Cases tab card's figures: a label over its value. */
export function Figure({
    labelId,
    strong = false,
    children,
}: {
    labelId: string;
    strong?: boolean;
    children: ReactNode;
}) {
    return (
        <div className="flex min-w-0 flex-col gap-1">
            <dt className={FIELD_LABEL_CLASS}>
                <FormattedMessage id={labelId} />
            </dt>
            <dd className={cn('text-body1 m-0 py-1 tabular-nums', strong ? 'font-semibold' : 'font-medium')}>
                {children}
            </dd>
        </div>
    );
}

export { AdvancedBillingSwitch, ITEM_TOTALS_BAR, ItemTableCard } from './ItemTableCard';
